import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from '../lib/api';

export interface InstagramAccount {
  id: string;
  ig_user_id: string;
  username: string;
  name: string | null;
  account_type: string | null;
  profile_picture_url: string | null;
  followers_count: number | null;
  status: string;
  webhook: { status: string; fields: string[]; subscribed_at: number | null };
  connected_at: number | null;
}

interface AccountContextValue {
  accounts: InstagramAccount[];
  selectedAccountId: string | null;
  selectedAccount: InstagramAccount | null;
  loading: boolean;
  selectAccount: (id: string | null) => void;
  refreshAccounts: () => Promise<void>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

const STORAGE_KEY = 'autodm:selectedAccount';

function readStoredId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeId(id: string | null): void {
  try {
    if (id === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, id);
    }
  } catch {
    // ignore
  }
}

function pickFirstAccount(list: InstagramAccount[], currentId: string | null): string | null {
  if (list.length === 0) return currentId;
  const exists = list.some((a) => a.id === currentId);
  if (exists) return currentId;
  return list[0].id;
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<InstagramAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(readStoredId);
  const [loading, setLoading] = useState(true);
  const selectedIdRef = useRef(selectedAccountId);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ accounts: InstagramAccount[] }>('/api/instagram/accounts')
      .then((data) => {
        if (cancelled) return;
        const list = data.accounts;
        setAccounts(list);
        const nextId = pickFirstAccount(list, selectedIdRef.current);
        if (nextId !== selectedIdRef.current) {
          selectedIdRef.current = nextId;
          setSelectedAccountId(nextId);
          storeId(nextId);
        }
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refreshAccounts = useCallback(async (): Promise<void> => {
    setReloadKey((k) => k + 1);
  }, []);

  const selectAccount = useCallback((id: string | null): void => {
    selectedIdRef.current = id;
    setSelectedAccountId(id);
    storeId(id);
  }, []);

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId) ?? null;

  return (
    <AccountContext.Provider
      value={{
        accounts,
        selectedAccountId,
        selectedAccount,
        loading,
        selectAccount,
        refreshAccounts,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export function useAccounts(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (ctx === null) {
    throw new Error('useAccounts must be used within AccountProvider');
  }
  return ctx;
}
